import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  FaPlus, FaStar, FaRegStar, FaUserEdit, FaGoogle, FaUserSecret, FaCheckCircle, FaShieldAlt,
} from "react-icons/fa";
import YouTubeEmbed from "./components/YouTubeEmbed";
import PaymentInfoModal from "./components/Specialist/PaymentInfoModal";
import {
  FiMessageCircle, FiDollarSign, FiCompass, FiCalendar, FiUsers,
  FiBriefcase, FiShield, FiHeart, FiRepeat, FiAward, FiTrendingUp, FiAlertCircle,
  FiClock, FiArrowUpCircle, FiInfo,
} from "react-icons/fi";
import Select from "react-select";
import LoginLinkedInButton from "./components/LoginLinkedInButton";
import CaptchaModal from "./components/CaptchaModal";
import EvaluationConfirmModal from "./components/EvaluationConfirmModal";
import RestrictableTextarea from "./components/RestrictableTextarea";
import WorkPeriodPicker from "./components/WorkPeriodPicker";
import SelectionProcessReviewForm from "./components/SelectionProcessReviewForm";
import SpellCheckSuggestions from "./components/SpellCheckSuggestions";
import { getCompanyLogoCandidates } from "./utils/getCompanyLogo";
import { handleAutoCorrectChange } from "./utils/ptBrAutoCorrect";
import {
  ensureSelectedProfileType,
  setSelectedProfileType,
} from "./services/profileType";

function CommentTextarea({
  value,
  onValueChange,
  containsPossiblePersonName,
  guidanceText,
  warningText,
  placeholder,
  rows = 3,
  className = "",
}) {
  const [draftValue, setDraftValue] = React.useState(() => (typeof value === "string" ? value : ""));
  const [showWarning, setShowWarning] = React.useState(false);
  const lastEmittedValueRef = React.useRef(typeof value === "string" ? value : "");

  React.useEffect(() => {
    const nextValue = typeof value === "string" ? value : "";
    if (nextValue !== lastEmittedValueRef.current) {
      setDraftValue(nextValue);
      lastEmittedValueRef.current = nextValue;
    }
  }, [value]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setShowWarning(containsPossiblePersonName(draftValue));

      if (draftValue !== lastEmittedValueRef.current) {
        onValueChange(draftValue);
        lastEmittedValueRef.current = draftValue;
      }
    }, 180);

    return () => window.clearTimeout(timer);
  }, [containsPossiblePersonName, draftValue, onValueChange]);

  const handleBlur = React.useCallback(() => {
    if (draftValue !== lastEmittedValueRef.current) {
      onValueChange(draftValue);
      lastEmittedValueRef.current = draftValue;
    }
  }, [draftValue, onValueChange]);

  return (
    <>
      <p className="text-xs text-slate-500 dark:text-slate-400">{guidanceText}</p>
      <textarea
        className={className}
        placeholder={placeholder}
        rows={rows}
        value={draftValue}
        onChange={(e) => handleAutoCorrectChange(e, draftValue, setDraftValue)}
        onBlur={handleBlur}
        spellCheck="true"
        lang="pt-BR"
        autoCorrect="on"
        autoCapitalize="sentences"
      />
      {showWarning && (
        <p className="text-xs text-yellow-700 dark:text-yellow-300 bg-yellow-100/80 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-md px-2 py-1 mt-1">
          {warningText}
        </p>
      )}
      <SpellCheckSuggestions value={draftValue} onChangeValue={setDraftValue} className="mt-1" />
    </>
  );
}

function OutlinedStar({ active, onClick, size = 18, label }) {
  const outlineScale = 1.24;
  const touchSize = size * 1.8;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        padding: 4,
        margin: 0,
        border: 0,
        background: "transparent",
        cursor: "pointer",
        lineHeight: 0,
        touchAction: "manipulation",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: touchSize,
        height: touchSize,
      }}
    >
      <span
        style={{ position: "relative", display: "inline-block", width: size, height: size, verticalAlign: "middle" }}
      >
        <span
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            transform: `scale(${outlineScale})`,
            transformOrigin: "center",
          }}
          aria-hidden="true"
        >
          <FaStar size={size} color="#000" />
        </span>
        <span style={{ position: "relative" }} aria-hidden="true">
          <FaStar size={size} color={active ? "#facc15" : "#e5e7eb"} />
        </span>
      </span>
    </button>
  );
}


function TrabalheiLaMobile({
  theme, toggleTheme, firebaseStatus,
  company, setCompany,
  rating, setRating,
  salario, setSalario,
  beneficios, setBeneficios,
  cultura, setCultura,
  oportunidades, setOportunidades,
  inovacao, setInovacao,
  lideranca, setLideranca,
  diversidade, setDiversidade,
  discriminacao, setDiscriminacao,
  cargaHoraria, setCargaHoraria,
  crescimento, setCrescimento,
  ambiente, setAmbiente,
  equilibrio, setEquilibrio,
  reconhecimento, setReconhecimento,
  comunicacao, setComunicacao,
  etica, setEtica,
  desenvolvimento, setDesenvolvimento,
  saudeBemEstar, setSaudeBemEstar,
  impactoSocial, setImpactoSocial,
  reputacao, setReputacao,
  estimacaoOrganizacao, setEstimacaoOrganizacao,
  commentRating, setCommentRating,
  commentSalario, setCommentSalario,
  commentBeneficios, setCommentBeneficios,
  commentCultura, setCommentCultura,
  commentOportunidades, setCommentOportunidades,
  commentInovacao, setCommentInovacao,
  commentLideranca, setCommentLideranca,
  commentDiversidade, setCommentDiversidade,
  commentDiscriminacao, setCommentDiscriminacao,
  commentCargaHoraria, setCommentCargaHoraria,
  commentCrescimento, setCommentCrescimento,
  commentAmbiente, setCommentAmbiente,
  commentEquilibrio, setCommentEquilibrio,
  commentReconhecimento, setCommentReconhecimento,
  commentComunicacao, setCommentComunicacao,
  commentEtica, setCommentEtica,
  commentDesenvolvimento, setCommentDesenvolvimento,
  commentSaudeBemEstar, setCommentSaudeBemEstar,
  commentImpactoSocial, setCommentImpactoSocial,
  commentReputacao, setCommentReputacao,
  commentEstimacaoOrganizacao, setCommentEstimacaoOrganizacao,
  entrySource, setEntrySource, contractType, setContractType, workModel, setWorkModel,
  workPeriodStartMonth, setWorkPeriodStartMonth, workPeriodStartYear, setWorkPeriodStartYear,
  workPeriodEndMonth, setWorkPeriodEndMonth, workPeriodEndYear, setWorkPeriodEndYear,
  workPeriodStillWorking, setWorkPeriodStillWorking,
  selectionProcessOnly, setSelectionProcessOnly,
  spClarity, setSpClarity,
  spCommunication, setSpCommunication,
  spResponseTime, setSpResponseTime,
  spDiscriminationFelt, setSpDiscriminationFelt,
  spDiscriminationComment, setSpDiscriminationComment,
  spEvidenceFiles, setSpEvidenceFiles,
  generalComment, setGeneralComment,
  generalCommentRestrictedSegments, setGeneralCommentRestrictedSegments,
  criterionRestrictedSegments, setSegmentsForCriterion,
  handleSubmit, isLoading,
  empresas, top3,
  showNewCompanyInput, setShowNewCompanyInput,
  handleAddNewCompany, handleConfirmNewCompany, pendingCompanyData, newCompanyCnpj, setNewCompanyCnpj, cnpjError,
  sectorFilter, setSectorFilter, setoresList,
  segmentFilter, setSegmentFilter, segmentosList,
  manualCompanyName, setManualCompanyName,
  manualSegment, setManualSegment,
  manualRazaoSocial, setManualRazaoSocial,
  cnaeSegmentOptions, isUserAdmin, handleAddCompanyWithoutCnpj,
  handleSaibaMais,
  linkedInClientId, linkedInRedirectUri,
  error, setError, isAuthenticated, userProfile, userPseudonym, onLoginSuccess, safeCompanyOptions,
  handleCompanyInputChange,
  handleLogout,
  onGoogleLogin,
  userVerificationLevel,
  selectedCompanyData,
  calcularMedia: calcularMediaProp,
  getMedalColor: getMedalColorProp,
  getMedalEmoji: getMedalEmojiProp,
  getBadgeColor: getBadgeColorProp,
  showCaptcha, setShowCaptcha, captchaConfirmed, setCaptchaConfirmed,
  handleCaptchaConfirmed,
  requestFindHelpRedirect,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Sinaliza que o próximo envio foi acionado por "Enviar e Buscar Ajuda".
  const findHelpAfterSubmitRef = React.useRef(false);
  const COMMENT_GUIDANCE_TEXT = "Descreva comportamentos e situações. Evite citar nomes de pessoas.";
  const COMMENT_WARNING_TEXT = "Identificamos possível citação de nome. Considere substituir por descrição do comportamento ou situação.";

  const containsPossiblePersonName = React.useCallback((value) => {
    const replacements = {
      "3": "e",
      "0": "o",
      "4": "a",
      "@": "a",
      "1": "i",
      "!": "i",
      "5": "s",
      "7": "t",
      "8": "b",
    };
    const text = String(value || "").replace(/[304@1!578]/g, (char) => replacements[char] || char);
    const namePattern = /\b[A-ZÁÀÂÃÉÊÍÓÔÕÚÇ][a-záàâãéêíóôõúç]+(?:\s+[A-ZÁÀÂÃÉÊÍÓÔÕÚÇ][a-záàâãéêíóôõúç]+)+\b/g;
    const sentenceBoundaryPattern = /[.!?\n]/;
    let match = namePattern.exec(text);

    while (match) {
      const idx = match.index;
      const before = text.slice(0, idx);
      const lastBoundary = Math.max(
        before.lastIndexOf("."),
        before.lastIndexOf("!"),
        before.lastIndexOf("?"),
        before.lastIndexOf("\n")
      );
      const segmentBeforeMatch = before.slice(lastBoundary + 1);

      if (sentenceBoundaryPattern.test(before) && segmentBeforeMatch.trim().length === 0) {
        match = namePattern.exec(text);
        continue;
      }

      if (segmentBeforeMatch.trim().length > 0) {
        return true;
      }

      match = namePattern.exec(text);
    }

    return false;
  }, []);

  const legacyMetricsBridge = {
    beneficios, setBeneficios,
    oportunidades, setOportunidades,
    inovacao, setInovacao,
    impactoSocial, setImpactoSocial,
    reputacao, setReputacao,
    estimacaoOrganizacao, setEstimacaoOrganizacao,
    commentBeneficios, setCommentBeneficios,
    commentOportunidades, setCommentOportunidades,
    commentInovacao, setCommentInovacao,
    commentImpactoSocial, setCommentImpactoSocial,
    commentReputacao, setCommentReputacao,
    commentEstimacaoOrganizacao, setCommentEstimacaoOrganizacao,
  };
  void legacyMetricsBridge;

  // Fallback local caso o pai (Home.js) não envie a função. Preferimos a
  // implementação injetada via props para manter paridade com Desktop e
  // herdar quaisquer ajustes futuros de pesos/critérios.
  const calcularMediaFallback = React.useCallback((emp) => {
    if (!emp) return "--";

    const ratings = [
      emp.rating, emp.salario, emp.beneficios, emp.cultura, emp.oportunidades,
      emp.inovacao, emp.lideranca, emp.diversidade, emp.ambiente, emp.equilibrio,
      emp.reconhecimento, emp.comunicacao, emp.etica, emp.desenvolvimento,
      emp.saudeBemEstar, emp.impactoSocial, emp.reputacao, emp.estimacaoOrganizacao,
    ].filter((val) => typeof val === "number" && !isNaN(val) && val > 0);

    if (ratings.length === 0) return "--";
    const sum = ratings.reduce((acc, curr) => acc + curr, 0);
    return (sum / ratings.length).toFixed(1);
  }, []);
  const calcularMedia = typeof calcularMediaProp === "function" ? calcularMediaProp : calcularMediaFallback;

  const logoCandidates = selectedCompanyData
    ? getCompanyLogoCandidates(selectedCompanyData.company, {
      size: 128,
      website: selectedCompanyData.website,
      logoUrl: selectedCompanyData.logoUrl,
    })
    : [];
  const [logoIndex, setLogoIndex] = React.useState(0);
  const companyLogoUrl = logoCandidates[logoIndex] || null;

  React.useEffect(() => {
    setLogoIndex(0);
  }, [selectedCompanyData?.company, selectedCompanyData?.website, selectedCompanyData?.logoUrl]);
  const companyAverage = selectedCompanyData ? calcularMedia(selectedCompanyData) : "--";
  const companyAverageValue = Number.parseFloat(companyAverage);
  const isCompanyRecommended = companyAverage !== "--" && Number.isFinite(companyAverageValue) && companyAverageValue >= 3;

  // Fallbacks locais — usados apenas se o pai não passar as funções via props.
  const getBadgeColorFallback = React.useCallback((media) => {
    if (media == null || media === "--") return "bg-slate-500";

    const numeric = typeof media === "number" ? media : Number(media);
    if (!Number.isFinite(numeric)) return "bg-slate-500";

    if (numeric >= 4.5) return "bg-emerald-700";
    if (numeric >= 4) return "bg-lime-600";
    if (numeric >= 3) return "bg-yellow-600";
    if (numeric >= 2) return "bg-purple-600";
    return "bg-red-600";
  }, []);

  const getMedalColorFallback = React.useCallback((index) => {
    if (index === 0) return "from-yellow-400 to-yellow-600";
    if (index === 1) return "from-gray-300 to-gray-500";
    if (index === 2) return "from-orange-300 to-orange-500";
    return "from-blue-300 to-blue-500";
  }, []);

  const getMedalEmojiFallback = React.useCallback((index) => {
    if (index === 0) return "🥇";
    if (index === 1) return "🥈";
    if (index === 2) return "🥉";
    return "🏅";
  }, []);

  const getBadgeColor = typeof getBadgeColorProp === "function" ? getBadgeColorProp : getBadgeColorFallback;
  const getMedalColor = typeof getMedalColorProp === "function" ? getMedalColorProp : getMedalColorFallback;
  const getMedalEmoji = typeof getMedalEmojiProp === "function" ? getMedalEmojiProp : getMedalEmojiFallback;

  const isDark = theme === "dark";
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "0.75rem",
      padding: "0.25rem",
      borderColor: state.isFocused ? "#1d4ed8" : (isDark ? "#475569" : "#e5e7eb"),
      boxShadow: state.isFocused ? "0 0 0 1px #1d4ed8" : "none",
      backgroundColor: isDark ? "#0f172a" : "#ffffff",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "0.75rem",
      overflow: "hidden",
      border: `1px solid ${isDark ? "#475569" : "#dbeafe"}`,
      backgroundColor: isDark ? "#0f172a" : "#ffffff",
      boxShadow: "0 10px 30px rgba(30, 58, 138, 0.12)",
    }),
    option: (base, state) => ({
      ...base,
      color: isDark ? "#e2e8f0" : "#1e3a8a",
      backgroundColor: state.isSelected ? (isDark ? "#1e3a8a" : "#dbeafe") : state.isFocused ? (isDark ? "#1e293b" : "#eff6ff") : (isDark ? "#0f172a" : "#ffffff"),
      fontWeight: state.isSelected ? 700 : 500,
      cursor: "pointer",
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? "#e2e8f0" : "#1e3a8a",
      fontWeight: 600,
    }),
    input: (base) => ({
      ...base,
      color: isDark ? "#e2e8f0" : "#1e3a8a",
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? "#94a3b8" : "#1e3a8a",
      fontWeight: 500,
    }),
  };

  const renderStars = (value, setValue, commentValue, setCommentValue, label, restrictKey) => (
    <div className="flex flex-col items-end w-full mt-2">
      <div className="flex items-center space-x-1 mb-2">
        {[...Array(5)].map((_, i) => (
          <OutlinedStar key={i} active={i < value} onClick={() => setValue(i + 1)} label={`${i + 1} estrelas`} />
        ))}
        <span className="ml-2 text-slate-700 dark:text-blue-200 font-medium">{value}/5</span>
      </div>
      {restrictKey ? (
        <RestrictableTextarea
          guidanceText={COMMENT_GUIDANCE_TEXT}
          warningText={COMMENT_WARNING_TEXT}
          containsPossiblePersonName={containsPossiblePersonName}
          className="w-full p-2 border border-gray-300 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-100 bg-white dark:bg-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500 mt-2"
          placeholder={`Comentário sobre ${label.toLowerCase()} (opcional). Selecione trechos para marcar como restritos.`}
          rows={3}
          value={commentValue}
          onValueChange={setCommentValue}
          segments={(criterionRestrictedSegments && criterionRestrictedSegments[restrictKey]) || []}
          onSegmentsChange={(segs) => setSegmentsForCriterion?.(restrictKey, segs)}
        />
      ) : (
        <CommentTextarea
        guidanceText={COMMENT_GUIDANCE_TEXT}
        warningText={COMMENT_WARNING_TEXT}
        containsPossiblePersonName={containsPossiblePersonName}
        className="w-full p-2 border border-gray-300 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-100 bg-white dark:bg-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500 mt-2"
        placeholder={`Comentário sobre ${label.toLowerCase()} (opcional)`}
        rows={3}
        value={commentValue}
        onValueChange={setCommentValue}
      />
      )}
    </div>
  );

  const renderYesNo = (value, setValue, commentValue, setCommentValue, label) => {
    const isRequired = value === "sim";
    const showError = isRequired && !commentValue.trim();
    return (
      <div className="flex flex-col w-full mt-2">
        <div className="flex items-center gap-3 mb-2" role="radiogroup" aria-label={label}>
          {[
            { v: "sim", l: t("Sim") },
            { v: "nao", l: t("Não") },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              role="radio"
              aria-checked={value === opt.v}
              onClick={() => setValue(opt.v)}
              className={`px-4 py-2 rounded-lg border text-sm font-semibold transition ${
                value === opt.v
                  ? (opt.v === "sim"
                      ? "bg-rose-600 text-white border-rose-600"
                      : "bg-emerald-600 text-white border-emerald-600")
                  : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
        {isRequired && (
          <p className="text-xs text-rose-600 dark:text-rose-400 mb-1">
            * Comentário obrigatório ao informar "Sim".
          </p>
        )}
        <CommentTextarea
          guidanceText={COMMENT_GUIDANCE_TEXT}
          warningText={COMMENT_WARNING_TEXT}
          containsPossiblePersonName={containsPossiblePersonName}
          className={`w-full p-2 border rounded-lg text-sm text-slate-700 dark:text-slate-100 bg-white dark:bg-slate-900 focus:outline-none focus:ring-1 mt-1 ${
            showError
              ? "border-rose-500 focus:ring-rose-500"
              : "border-gray-300 dark:border-slate-700 focus:ring-blue-500"
          }`}
          placeholder={`Comentário sobre ${label.toLowerCase()}${isRequired ? " (obrigatório)" : " (opcional)"}`}
          rows={3}
          value={commentValue}
          onValueChange={setCommentValue}
        />
      </div>
    );
  };

  const campos = [
    { restrictKey: "comunicacao", label: t("Processo de Recrutamento"), icon: <FiMessageCircle className="text-cyan-700" />, iconBg: "from-cyan-50 to-sky-100 border-cyan-200", value: comunicacao, set: setComunicacao, comment: commentComunicacao, setComment: setCommentComunicacao },
    { restrictKey: "etica", label: t("Proposta salarial e benefícios"), icon: <FiDollarSign className="text-emerald-600" />, iconBg: "from-emerald-50 to-lime-100 border-emerald-200", value: etica, set: setEtica, comment: commentEtica, setComment: setCommentEtica },
    { restrictKey: "cultura", label: t("Visão e valores da empresa"), icon: <FiCompass className="text-slate-700" />, iconBg: "from-slate-50 to-slate-100 border-slate-300", value: cultura, set: setCultura, comment: commentCultura, setComment: setCommentCultura },
    { restrictKey: "salario", label: t("Data do Pagamento"), icon: <FiCalendar className="text-rose-600" />, iconBg: "from-rose-50 to-red-100 border-rose-200", value: salario, set: setSalario, comment: commentSalario, setComment: setCommentSalario },
    { restrictKey: "lideranca", label: t("Acessibilidade e respeito da liderança"), icon: <FiUsers className="text-violet-700" />, iconBg: "from-violet-50 to-indigo-100 border-violet-200", value: lideranca, set: setLideranca, comment: commentLideranca, setComment: setCommentLideranca },
    { restrictKey: "estimacaoOrganizacao", label: t("Condições de trabalho"), icon: <FiBriefcase className="text-blue-600" />, iconBg: "from-blue-50 to-indigo-100 border-blue-200", value: estimacaoOrganizacao, set: setEstimacaoOrganizacao, comment: commentEstimacaoOrganizacao, setComment: setCommentEstimacaoOrganizacao },
    { restrictKey: "ambiente", label: t("Estímulo ao respeito"), icon: <FiUsers className="text-teal-700" />, iconBg: "from-teal-50 to-emerald-100 border-teal-200", value: ambiente, set: setAmbiente, comment: commentAmbiente, setComment: setCommentAmbiente },
    { restrictKey: "discriminacao", label: t("Sofreu discriminação?"), icon: <FiAlertCircle className="text-cyan-700" />, iconBg: "from-cyan-50 to-sky-100 border-cyan-200", value: discriminacao, set: setDiscriminacao, comment: commentDiscriminacao, setComment: setCommentDiscriminacao },
    { restrictKey: "diversidade", label: t("Diversidade e Inclusão"), icon: <FiUsers className="text-pink-600" />, iconBg: "from-pink-50 to-rose-100 border-pink-200", value: diversidade, set: setDiversidade, comment: commentDiversidade, setComment: setCommentDiversidade },
    { restrictKey: "cargaHoraria", label: t("Carga Horária / Jornada de Trabalho"), icon: <FiClock className="text-indigo-700" />, iconBg: "from-indigo-50 to-blue-100 border-indigo-200", value: cargaHoraria, set: setCargaHoraria, comment: commentCargaHoraria, setComment: setCommentCargaHoraria },
    { restrictKey: "crescimento", label: t("Oportunidades de Desenvolvimento / Crescimento"), icon: <FiArrowUpCircle className="text-emerald-700" />, iconBg: "from-emerald-50 to-teal-100 border-emerald-200", value: crescimento, set: setCrescimento, comment: commentCrescimento, setComment: setCommentCrescimento },
    { restrictKey: "rating", label: t("Segurança e integridade"), icon: <FiShield className="text-amber-600" />, iconBg: "from-amber-50 to-yellow-100 border-amber-200", value: rating, set: setRating, comment: commentRating, setComment: setCommentRating },
    { restrictKey: "saudeBemEstar", label: t("Preocupação com o bem estar"), icon: <FiHeart className="text-red-600" />, iconBg: "from-red-50 to-rose-100 border-red-200", value: saudeBemEstar, set: setSaudeBemEstar, comment: commentSaudeBemEstar, setComment: setCommentSaudeBemEstar },
    { restrictKey: "equilibrio", label: t("Rotatividade"), subtitle: t("(Demite com facilidade?)"), icon: <FiRepeat className="text-slate-700" />, iconBg: "from-slate-50 to-gray-100 border-slate-300", value: equilibrio, set: setEquilibrio, comment: commentEquilibrio, setComment: setCommentEquilibrio },
    { restrictKey: "reconhecimento", label: t("Reconhecimento"), icon: <FiAward className="text-amber-600" />, iconBg: "from-amber-50 to-orange-100 border-amber-200", value: reconhecimento, set: setReconhecimento, comment: commentReconhecimento, setComment: setCommentReconhecimento },
    { restrictKey: "desenvolvimento", label: t("Planos de cargos e salários"), icon: <FiTrendingUp className="text-fuchsia-700" />, iconBg: "from-fuchsia-50 to-pink-100 border-fuchsia-200", value: desenvolvimento, set: setDesenvolvimento, comment: commentDesenvolvimento, setComment: setCommentDesenvolvimento },
  ];

  // Um critério é considerado preenchido quando possui nota (>= 1 estrela) ou,
  // no caso do campo sim/não, quando uma opção foi selecionada.
  const isCampoFilled = (campo) =>
    campo.type === "yesno" ? Boolean(campo.value) : Number(campo.value) >= 1;

  const missingCampos = campos.filter((c) => !isCampoFilled(c));

  // Intercepta o submit: valida todos os critérios obrigatórios e, quando ok,
  // abre o modal de confirmação antes de repassar para o handleSubmit original.
  const handleValidatedSubmit = (e) => {
    e.preventDefault();
    if (!selectionProcessOnly && missingCampos.length > 0) {
      setShowValidation(true);
      setError(
        `Preencha todos os critérios obrigatórios antes de enviar. Faltam: ${missingCampos
          .map((c) => c.label)
          .join(", ")}.`
      );
      return;
    }
    setShowValidation(false);
    setError(null);
    setShowConfirmModal(true);
  };

  const handleConfirmSend = () => {
    setShowConfirmModal(false);
    requestFindHelpRedirect?.(findHelpAfterSubmitRef.current);
    findHelpAfterSubmitRef.current = false;
    handleSubmit({ preventDefault: () => {} });
  };

  // Progress bar: IntersectionObserver para critérios
  const criterionRefs = React.useRef([]);
  const [visibleCriterionIdx, setVisibleCriterionIdx] = React.useState(-1);

  React.useEffect(() => {
    const refs = criterionRefs.current;
    if (!refs.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        let maxIdx = -1;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.criterionIdx);
            if (idx > maxIdx) maxIdx = idx;
          }
        }
        if (maxIdx >= 0) setVisibleCriterionIdx(maxIdx);
      },
      { threshold: 0.5 }
    );
    for (const el of refs) { if (el) observer.observe(el); }
    return () => observer.disconnect();
  }, [campos.length]);

  const sourceConfig = [
    { key: "indicacao", label: "Indicação", color: "#2563eb" },
    { key: "siteVagas", label: "Site de vagas", color: "#16a34a" },
    { key: "gruposWhatsapp", label: "WhatsApp", color: "#d97706" },
    { key: "redesSociais", label: "Redes sociais", color: "#9333ea" },
  ];

  const contractConfig = [
    { key: "pj", label: "PJ", color: "#0284c7" },
    { key: "clt", label: "CLT", color: "#16a34a" },
  ];

  const workModelConfig = [
    { key: "presencial", label: "Presencial", color: "#2563eb" },
    { key: "hibrida", label: "Híbrida (Semi presencial)", color: "#d97706" },
    { key: "remota", label: "Remota", color: "#16a34a" },
  ];

  const buildPieData = (stats, config) => {
    const total = config.reduce((sum, item) => sum + (stats?.[item.key] || 0), 0);
    if (!total) return { chart: "#e5e7eb 0deg 360deg", items: config.map((item) => ({ ...item, percent: 0 })) };

    let cursor = 0;
    const items = config.map((item) => {
      const value = stats?.[item.key] || 0;
      const percent = (value / total) * 100;
      const deg = (percent / 100) * 360;
      const start = cursor;
      cursor += deg;
      return { ...item, percent, slice: `${item.color} ${start.toFixed(2)}deg ${cursor.toFixed(2)}deg` };
    });

    return { chart: items.filter((item) => item.percent > 0).map((item) => item.slice).join(", "), items };
  };

  const sourcePieData = buildPieData(selectedCompanyData?.sourceStats, sourceConfig);
  const contractPieData = buildPieData(selectedCompanyData?.contractStats, contractConfig);
  const workModelPieData = buildPieData(selectedCompanyData?.workModelStats, workModelConfig);
  const hasCompletedProfile = Boolean((userPseudonym || "").toString().trim());
  const headerRef = React.useRef(null);
  const [headerSpacerHeight, setHeaderSpacerHeight] = React.useState(0);
  // Offset dinâmico para a barra de progresso sticky — alinha com a altura
  // atual do header (paridade com Desktop), evitando sobreposição visual.
  const stickyProgressTop = headerSpacerHeight + 8;
  const [showPaymentInfo, setShowPaymentInfo] = React.useState(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [showValidation, setShowValidation] = React.useState(false);

  // Garante perfil padrão ("worker") em sessionStorage no boot da Home.
  React.useEffect(() => {
    ensureSelectedProfileType();
  }, []);

  // Clique nos cards de perfil: persiste a escolha e roteia conforme o tipo.
  // - Trabalhador: cadastro manual em /pseudonym?manual=1.
  // - Especialista: landing dedicada em /apoiadores (destino distinto do
  //   trabalhador, conforme comportamento histórico do botão).
  const handleChooseProfile = React.useCallback(
    (type) => {
      setSelectedProfileType(type);
      if (type === "specialist") {
        navigate("/apoiadores");
      } else {
        navigate("/pseudonym?manual=1");
      }
    },
    [navigate]
  );

  const getTopSliceLabel = (pieData) => {
    const topItem = pieData.items.reduce((best, current) => (current.percent > best.percent ? current : best), pieData.items[0]);
    if (!topItem || topItem.percent <= 0) {
      return "Ainda sem dados suficientes";
    }
    return `${topItem.label} lidera com ${topItem.percent.toFixed(0)}%`;
  };

  React.useEffect(() => {
    const updateHeaderSpacer = () => {
      setHeaderSpacerHeight(headerRef.current?.offsetHeight || 0);
    };

    updateHeaderSpacer();
    window.addEventListener("resize", updateHeaderSpacer);

    return () => {
      window.removeEventListener("resize", updateHeaderSpacer);
    };
  }, [company, firebaseStatus, hasCompletedProfile, isAuthenticated, theme, userProfile?.avatar, userProfile?.name, userProfile?.verification?.certified, userPseudonym]);

  return (
    <div
      className={`min-h-screen font-sans pb-10 ${
        theme === "dark"
          ? "bg-slate-800"
          : "bg-gradient-to-b from-blue-50 via-sky-50 to-blue-100"
      }`}
    >
      <header ref={headerRef} className="fixed top-0 left-0 right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm px-2 py-1 z-50 border-b border-blue-100 dark:border-slate-700">
        {/* ── Linha do título + ícones ── */}
        <div className="flex items-center w-full" style={{ gap: 8 }}>
          <FaStar className="text-amber-500" style={{ width: 24, height: 24, flexShrink: 0 }} />
          <span
            className="logo-syne leading-none whitespace-nowrap"
            style={{
              fontSize: 'clamp(18px, 4.5vw, 24px)',
              fontWeight: 900,
              letterSpacing: 'normal',
              color: theme === 'dark' ? '#90caf9' : '#1a237e',
              flex: 1,
              textAlign: 'center',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <span>TRABALHEI </span>
            <span style={{ color: '#FFC107' }}>LÁ</span>
          </span>
          <button
            type="button"
            onClick={toggleTheme}
            className="bg-slate-200 dark:bg-slate-700 rounded-full font-semibold text-slate-700 dark:text-slate-100 hover:bg-slate-300 dark:hover:bg-slate-600 transition"
            style={{ width: 24, height: 24, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, fontSize: '1rem', lineHeight: 1 }}
            aria-label="Alternar tema claro/escuro"
          >
            {theme === "dark" ? "☀️" : "🌙"}
          </button>
          {!isAuthenticated && (
            <button
              type="button"
              onClick={() => navigate("/login")}
              aria-label="Entrar"
              className="ml-2 px-3 py-1 rounded-full bg-blue-700 hover:bg-blue-800 text-white font-bold text-xs shadow-md whitespace-nowrap transition dark:bg-blue-600 dark:hover:bg-blue-700"
              style={{ flexShrink: 0 }}
            >
              Entrar
            </button>
          )}
        </div>
        <div className="w-20 h-0.5 mx-auto rounded-full bg-gradient-to-r from-blue-300 via-blue-600 to-blue-300 dark:from-slate-500 dark:via-blue-400 dark:to-slate-500" />

        {firebaseStatus && <p className="text-[11px] text-red-500 dark:text-red-400 mt-1 text-center">{firebaseStatus}</p>}
      </header>

      <div style={{ height: headerSpacerHeight }} />

      <main className="px-4 space-y-3">
        {/* Card empresa pesquisada — logo grande, nota e classificação visíveis */}
        {company && (
          <div className="w-full rounded-2xl border border-blue-100 dark:border-slate-700 bg-blue-50/60 dark:bg-slate-800/70 p-3">
            <div className="flex items-center gap-3">
              {/* Logo grande */}
              <div className={`shrink-0 rounded-2xl flex items-center justify-center border-2 overflow-hidden ${companyLogoUrl ? 'bg-blue-50 dark:bg-slate-800 border-blue-200 dark:border-slate-600' : 'bg-blue-900 border-blue-700'}`} style={{ width: 80, height: 80 }}>
                {companyLogoUrl ? (
                  <img
                    src={companyLogoUrl}
                    alt="Logo da empresa"
                    className="w-full h-full object-contain p-1"
                    onError={(e) => {
                      if (logoIndex < logoCandidates.length - 1) {
                        setLogoIndex((prev) => prev + 1);
                      } else {
                        const name = selectedCompanyData?.company || "";
                        const initials = name
                          .split(/\s+/).filter(Boolean).slice(0, 2)
                          .map((w) => w[0]).join("").toUpperCase() || "?";
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=1a237e&color=fff&size=160&bold=true`;
                      }
                    }}
                  />
                ) : (
                  <span className="text-white font-black text-2xl tracking-tight">TL</span>
                )}
              </div>

              {/* Nome + classificação (centro) */}
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-blue-800 dark:text-blue-100 leading-tight break-words">
                  {company.value}
                </p>
                {companyAverage !== "--" && (
                  <p
                    className={`inline-block mt-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                      isCompanyRecommended
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-red-50 text-red-700 border-red-200"
                    }`}
                  >
                    {isCompanyRecommended ? "✓ Acima da média" : "✗ Abaixo da média"}
                  </p>
                )}
              </div>

              {/* Nota grande (avatar circular) */}
              <div className="shrink-0 flex flex-col items-center">
                <div
                  className={`rounded-full flex items-center justify-center shadow-md border-2 ${
                    companyAverage === "--"
                      ? "bg-slate-500 border-slate-300"
                      : `${getBadgeColor(companyAverage)} border-white/40`
                  }`}
                  style={{ width: 64, height: 64 }}
                >
                  <div className="text-center leading-none">
                    <p className="text-xl font-extrabold text-white">{companyAverage}</p>
                    {companyAverage !== "--" && (
                      <p className="text-[9px] text-white/90 mt-0.5">de 5</p>
                    )}
                  </div>
                </div>
                <p className="mt-1 text-[10px] font-bold tracking-widest text-blue-700 dark:text-blue-300">NOTA</p>
              </div>
            </div>

            {/* Botão "Ver avaliações" abaixo */}
            <button
              type="button"
              onClick={handleSaibaMais}
              className="mt-3 w-full py-2 px-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition"
              style={{ fontSize: '0.9rem' }}
            >
              Ver avaliações
            </button>
          </div>
        )}

        {/* Perfil do usuário */}
        <div className="w-full flex items-center justify-between gap-2">
          {isAuthenticated ? (
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={() => navigate("/minha-conta")}
                title="Ir para Minha conta"
                className="w-9 h-9 rounded-full bg-blue-100 dark:bg-slate-700 flex items-center justify-center text-lg overflow-hidden hover:opacity-80 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {userProfile?.avatar ? (
                  typeof userProfile.avatar === "string" && (userProfile.avatar.startsWith("data:") || userProfile.avatar.startsWith("http")) ? (
                    <img src={userProfile.avatar} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span>{userProfile.avatar}</span>
                  )
                ) : (
                  <span className="text-blue-600">👤</span>
                )}
              </button>
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => navigate("/minha-conta")}
                  title="Ir para Minha conta"
                  className="text-sm font-semibold text-slate-700 dark:text-blue-100 truncate hover:underline focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
                >
                  {userPseudonym || "Anônimo"}
                </button>
                {userProfile?.verification?.certified && (
                  <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                    ✓ Certificado
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-1 mt-0.5">
                  {(() => {
                    const role = (userProfile?.role || "").toString().toLowerCase().trim();
                    const userType = (userProfile?.userType || "").toString().toLowerCase().trim();
                    const isEmployer =
                      role === "admin_empresa" ||
                      userType === "empresario" ||
                      userType === "empres\u00e1rio" ||
                      userProfile?.isEmployer === true ||
                      Boolean(userProfile?.managedCompanyId);
                    if (isEmployer) return null;
                    return (
                      <a
                        href="/pseudonym"
                        className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-300 text-emerald-900 text-[11px] font-medium hover:bg-emerald-400 shadow-sm transition"
                      >
                        <FaUserEdit className="mr-1 text-[10px]" />
                        {hasCompletedProfile ? "Editar perfil" : "Crie seu perfil"}
                      </a>
                    );
                  })()}
                  <a
                    href="/minha-conta"
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-200 text-blue-900 text-[11px] font-medium hover:bg-blue-300 shadow-sm transition"
                  >
                    Minha conta
                  </a>
                </div>
                {/*
                  Botão "Painel Empresa" removido do cabeçalho mobile para
                  manter consistência com a versão desktop (que não exibe
                  esse atalho no header). O acesso ao painel da empresa
                  segue disponível pelo card "Bem-vindo(a)" do Hero
                  (CENÁRIO 3) e pelo link "Para Empresas" no rodapé.
                */}
                {(() => {
                  try {
                    const { isAdmin } = require("./utils/rbac");
                    if (isAdmin()) {
                      return (
                        <a
                          href="/admin"
                          className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition"
                        >
                          Admin
                        </a>
                      );
                    }
                  } catch { /* silencioso */ }
                  return null;
                })()}
              </div>
            </div>
          ) : (
            <div />
          )}
        </div>

        {/* HERO CARD MOBILE — unifica login + crie sua conta + saudação */}
        {(() => {
          const role = (userProfile?.role || "").toString().toLowerCase().trim();
          const userType = (userProfile?.userType || "").toString().toLowerCase().trim();
          // Tipo de perfil escolhido na seleção inicial (gravado em
          // `profileTypeChosen` no fim do cadastro). Garante que, após a
          // escolha, a Home pare de exibir "Escolha seu perfil".
          const chosenType = (userProfile?.profileTypeChosen || "").toString().toLowerCase().trim();
          const isEmployerProfile =
            role === "admin_empresa" ||
            userType === "empresario" ||
            userType === "empres\u00e1rio" ||
            userProfile?.isEmployer === true ||
            Boolean(userProfile?.managedCompanyId);
          const isSpecialistProfile =
            userType === "apoiador" ||
            userType === "especialista" ||
            chosenType === "specialist" ||
            chosenType === "apoiador" ||
            chosenType === "especialista" ||
            Boolean(userProfile?.apoiadorId);
          const greetingName =
            (userPseudonym || userProfile?.name || "").toString().trim() || "amigo(a)";
          let isAdminFlag = false;
          try {
            // eslint-disable-next-line global-require
            const { isAdmin } = require("./utils/rbac");
            isAdminFlag = isAdmin();
          } catch { /* silencioso */ }
          return (
            <section
              className="mx-auto w-full rounded-2xl shadow-xl p-4 border-2 border-blue-400 dark:border-blue-500/60 bg-gradient-to-br from-blue-600 via-indigo-700 to-blue-800 dark:from-slate-800 dark:via-slate-900 dark:to-blue-950 ring-1 ring-white/10"
              style={{ animation: "homeLoginSectionIn 700ms ease-out both" }}
            >
              <style>{`
                @keyframes homeLoginSectionIn {
                  from { opacity: 0; transform: translateY(16px); }
                  to { opacity: 1; transform: translateY(0); }
                }
              `}</style>
              {/*
                Hero redesenhado para reduzir atrito de cadastro:
                - Headline forte com benefício direto
                - Subtítulo objetivo (apenas para visitantes/não convertidos)
                - Badges grandes e vídeo grande saíram daqui:
                  • badges viraram um "trust strip" discreto após os botões (cenários 1 e 2)
                  • vídeo foi para a seção "Como funciona?" mais abaixo
              */}
              <h2 className="text-lg md:text-xl font-extrabold text-white text-center mb-2 tracking-tight drop-shadow leading-snug">
                Avalie suas experiências profissionais de forma 100% anônima e segura.
              </h2>
              {!isAuthenticated && (
                <p className="text-xs md:text-sm text-blue-100/90 text-center mb-3 leading-relaxed font-semibold">
                  Qual é o seu perfil para começar?
                </p>
              )}
              <div className="w-16 h-1 mx-auto mb-4 rounded-full bg-gradient-to-r from-amber-300 via-amber-400 to-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.6)]" />

              {/* CENÁRIO 1 — não autenticado: escolha de perfil + login social secundário */}
              {!isAuthenticated && (
                <>
                  <div className="flex flex-col gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => handleChooseProfile("worker")}
                      className="relative w-full flex flex-col items-center justify-center text-center py-2.5 px-3 rounded-xl bg-lime-400 text-emerald-950 text-sm font-bold shadow ring-2 ring-amber-300/70 transition-all duration-200 hover:bg-lime-500 hover:scale-[1.02] hover:shadow-lg active:scale-100 focus:outline-none focus:ring-2 focus:ring-lime-300"
                    >
                      <span className="absolute -top-2 right-2 inline-flex items-center gap-0.5 bg-amber-400 text-amber-950 text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full shadow-sm">★ Recomendado</span>
                      <span>Sou Trabalhador</span>
                      <span className="block text-[10px] font-medium text-emerald-900/80 mt-0.5">(avalia anonimamente)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChooseProfile("specialist")}
                      className="w-full flex flex-col items-center justify-center text-center py-2.5 px-3 rounded-xl bg-white text-blue-800 text-sm font-bold shadow transition-all duration-200 hover:bg-blue-50 hover:scale-[1.02] hover:shadow-lg active:scale-100 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    >
                      <span>Sou Especialista</span>
                      <span className="block text-[10px] font-medium text-blue-700/80 mt-0.5">(advogados, psicólogos, consultores e outros)</span>
                    </button>
                  </div>
                </>
              )}

              {/* Trust strip — sinaliza Anônimo/Verificado/Confiável de forma discreta
                  apenas para visitantes (não logados). */}
              {!isAuthenticated && (
                <div className="mt-4 pt-3 border-t border-white/15 flex flex-wrap items-center justify-center gap-1.5">
                  <span
                    title="Sua identidade é mantida em sigilo."
                    className="inline-flex items-center gap-1 bg-white/10 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold"
                  >
                    <FaUserSecret aria-hidden="true" /> Anônimo
                  </span>
                  <span
                    title="Conta verificada via LinkedIn ou Google."
                    className="inline-flex items-center gap-1 bg-white/10 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold"
                  >
                    <FaCheckCircle aria-hidden="true" /> Verificado
                  </span>
                  <span
                    title="Plataforma com moderação e anti-fraude."
                    className="inline-flex items-center gap-1 bg-white/10 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold"
                  >
                    <FaShieldAlt aria-hidden="true" /> Confiável
                  </span>
                </div>
              )}

              {/* CENÁRIO 3 — autenticado: home da conta (Buscar ajuda / Minha conta / Sair) */}
              {isAuthenticated && (
                <>
                  <p className="text-xs text-blue-100 text-center mb-3">
                    Bem-vindo(a), <span className="font-extrabold text-white">{greetingName}</span>!
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    {isEmployerProfile ? (
                      <button
                        type="button"
                        onClick={() => navigate("/empresa-dashboard")}
                        className="inline-flex items-center px-3 py-2 rounded-lg bg-white text-blue-800 text-xs font-bold shadow hover:bg-blue-50 transition"
                      >
                        Painel Empresa
                      </button>
                    ) : isSpecialistProfile ? null : (
                      <a
                        href="/trabalhador/encontrar-especialista"
                        className="inline-flex items-center px-3 py-2 rounded-lg bg-white text-blue-800 text-xs font-bold shadow hover:bg-blue-50 transition"
                      >
                        Buscar ajuda
                      </a>
                    )}
                    {isSpecialistProfile ? (
                      <>
                        <button
                          type="button"
                          onClick={() => navigate("/apoiador/my-contacts")}
                          className="inline-flex items-center justify-center min-h-[40px] px-3 py-2 rounded-lg bg-white text-blue-800 text-xs font-bold shadow hover:bg-blue-50 transition"
                        >
                          Meu Painel
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const aid = userProfile?.apoiadorId || "";
                            if (aid) {
                              navigate(`/apoiadores/perfil/${encodeURIComponent(aid)}`);
                            } else {
                              navigate("/apoiador/my-contacts");
                            }
                          }}
                          className="inline-flex items-center justify-center min-h-[40px] px-3 py-2 rounded-lg bg-transparent border-2 border-white text-white text-xs font-bold hover:bg-white/10 transition"
                        >
                          Meu Perfil Público
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => navigate("/minha-conta")}
                        className="inline-flex items-center px-3 py-2 rounded-lg bg-white text-blue-800 text-xs font-bold shadow hover:bg-blue-50 transition"
                      >
                        Minha conta
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="inline-flex items-center px-3 py-2 rounded-lg bg-transparent border-2 border-white text-white text-xs font-bold hover:bg-white/10 transition"
                    >
                      Sair
                    </button>
                    {isAdminFlag && (
                      <a
                        href="/admin"
                        className="text-[11px] text-blue-100 hover:text-white underline transition"
                      >
                        Admin
                      </a>
                    )}
                  </div>
                </>
              )}

              {/* Premium — sempre visível na base do Hero */}
              <div className="mt-5 pt-4 border-t border-white/20 flex justify-center">
                <button
                  type="button"
                  onClick={() => navigate('/escolha-perfil?planos=1')}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 text-amber-950 text-[11px] leading-snug font-bold shadow-md ring-1 ring-amber-300/60 hover:shadow-lg transition-all"
                  aria-label="Ver benefícios do plano Premium"
                >
                  <span aria-hidden="true" className="flex-shrink-0">⭐</span>
                  <span className="flex-1 text-center">Premium para Trabalhadores e Especialistas</span>
                  <span aria-hidden="true" className="flex-shrink-0">›</span>
                </button>
              </div>
            </section>
          );
        })()}

        {/* Como funciona? — vídeo de apresentação movido do topo do Hero para
            reduzir atrito de cadastro. Fica visível para quem rolar a página
            em busca de mais informações. */}
        <section className="mt-6 bg-white dark:bg-slate-900 rounded-2xl shadow-md p-5 border border-blue-100 dark:border-slate-700">
          <h3 className="text-sm uppercase tracking-[0.14em] font-extrabold text-blue-800 dark:text-blue-200 text-center mb-2">
            Como funciona?
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-300 text-center mb-3">
            Em 2 minutos, veja como avaliar empresas de forma anônima e verificada.
          </p>
          <YouTubeEmbed videoId="BOUKyTc0cns" title="Apresentação Trabalhei Lá" />
        </section>

        {/* FORMULÁRIO */}
        <section className="mt-6 bg-white dark:bg-slate-900 rounded-2xl shadow-md p-5 border border-blue-50 dark:border-slate-700">
          <h2 className="text-sm uppercase tracking-[0.14em] font-extrabold text-blue-800 dark:text-blue-200 text-center mb-3">Avaliar Empresa</h2>
          {isAuthenticated && userVerificationLevel === "free" && (
            <div className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10 p-4 text-center">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                Para avaliar uma empresa, faça login com sua conta LinkedIn ou Google. Isso garante a autenticidade das avaliações sem revelar sua identidade.
              </p>
              <div className="mt-3 flex flex-col gap-2 items-stretch">
                <LoginLinkedInButton
                  clientId={linkedInClientId}
                  redirectUri={linkedInRedirectUri}
                  onLoginSuccess={onLoginSuccess}
                  onLoginFailure={(err) => setError(err?.message || String(err))}
                />
                <button
                  type="button"
                  onClick={onGoogleLogin}
                  className="w-full inline-flex items-center justify-center gap-2 bg-white dark:bg-slate-900 border border-blue-200 dark:border-slate-700 text-blue-800 dark:text-blue-200 font-semibold py-2 px-4 rounded-lg shadow hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors text-sm"
                >
                  <FaGoogle className="text-base" /> Entrar com Google
                </button>
              </div>
            </div>
          )}
          <form onSubmit={handleValidatedSubmit} className="space-y-5">
            <div>
              <label className="font-semibold text-slate-700 dark:text-blue-200 mb-2 block text-sm">Selecione a Empresa</label>
              <Select
                options={safeCompanyOptions}
                value={company}
                onChange={setCompany}
                onInputChange={handleCompanyInputChange}
                placeholder="Buscar por nome ou CNPJ..."
                styles={selectStyles}
                isClearable
                noOptionsMessage={({ inputValue }) =>
                  inputValue && inputValue.length >= 2
                    ? "Nenhuma empresa encontrada. Use 'Adicionar nova empresa' abaixo."
                    : "Digite para buscar uma empresa"
                }
                filterOption={(option, rawInput) => {
                  const input = (rawInput || "").toString().trim().toLowerCase();
                  if (!input) return true;
                  const data = option?.data || {};
                  const label = (option?.label || data.label || "").toLowerCase();
                  const razao = (data.razaoSocial || "").toLowerCase();
                  const cnpjDigits = String(data.cnpj || "");
                  const cnpjFormatted = (data.cnpjFormatted || "").toLowerCase();
                  const inputDigits = input.replace(/\D/g, "");
                  if (label.includes(input) || razao.includes(input) || cnpjFormatted.includes(input)) return true;
                  if (inputDigits && cnpjDigits.includes(inputDigits)) return true;
                  return false;
                }}
              />

              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setShowNewCompanyInput(!showNewCompanyInput)}
                  className={`inline-flex items-center justify-center bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition ${showNewCompanyInput ? "w-auto py-1.5 px-4 text-sm" : "w-full py-2"}`}
                >
                  {showNewCompanyInput ? "Cancelar" : "Adicione a empresa"}
                </button>
              </div>

              {showNewCompanyInput && (
                <div className="mt-3 space-y-2">
                  <input
                    value={newCompanyCnpj}
                    onChange={(e) => {
                      const d = e.target.value.replace(/\D/g, "").slice(0, 14);
                      let m = d;
                      if (d.length > 12) m = `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
                      else if (d.length > 8) m = `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
                      else if (d.length > 5) m = `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
                      else if (d.length > 2) m = `${d.slice(0,2)}.${d.slice(2)}`;
                      setNewCompanyCnpj(m);
                    }}
                    placeholder="00.000.000/0001-00"
                    className="w-full p-3 border border-gray-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    inputMode="numeric"
                    autoComplete="off"
                  />
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    Não sabe o CNPJ? Pesquise o nome da empresa no Google seguido de CNPJ.
                  </p>
                  {cnpjError && <p className="text-sm text-red-600">{cnpjError}</p>}

                  <button
                    type="button"
                    onClick={handleAddNewCompany}
                    disabled={isLoading}
                    className="w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition"
                  >
                    {isLoading ? "Consultando CNPJ..." : "Consultar CNPJ"}
                  </button>

                  {pendingCompanyData && (
                    <div className="bg-blue-50 dark:bg-slate-800 border border-blue-200 dark:border-slate-700 rounded-xl p-3 text-sm text-blue-900 dark:text-blue-100">
                      <p className="font-semibold">Empresa encontrada: {pendingCompanyData.company}</p>
                      <p className="text-xs text-blue-700 mt-1">CNPJ: {pendingCompanyData.cnpj || "não informado"}</p>
                      {pendingCompanyData.razaoSocial && (
                        <p className="text-xs text-blue-700 mt-0.5">Razão social: {pendingCompanyData.razaoSocial}</p>
                      )}
                      {pendingCompanyData.segmento && (
                        <p className="text-xs text-indigo-700 mt-0.5">Segmento (CNAE): {pendingCompanyData.segmento}</p>
                      )}
                      {pendingCompanyData.cnaeDescricao && (
                        <p className="text-xs text-emerald-700 mt-0.5">Setor: {pendingCompanyData.cnaeDescricao}</p>
                      )}
                      <p className="mt-2 font-medium">👍 Está correto?</p>
                      <button
                        type="button"
                        onClick={handleConfirmNewCompany}
                        disabled={isLoading}
                        className="mt-2 w-full py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition"
                      >
                        {isLoading ? "Confirmando..." : "Confirmar empresa"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 grid grid-cols-1 gap-3">
                <div className="bg-blue-50 dark:bg-slate-800 border border-blue-200 dark:border-slate-700 rounded-xl p-3">
                    <p className="text-sm font-bold text-blue-800 dark:text-blue-200 mb-2">Como entrou na empresa?</p>
                  <div className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
                    {sourceConfig.map((item) => (
                      <label key={item.key} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="entrySourceMobile"
                          checked={entrySource === item.key}
                          onChange={() => setEntrySource(item.key)}
                        />
                        {item.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-slate-800 border border-blue-200 dark:border-slate-700 rounded-xl p-3">
                  <p className="text-sm font-bold text-blue-800 dark:text-blue-200 mb-2">Forma de contratação</p>
                  <div className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
                    {contractConfig.map((item) => (
                      <label key={item.key} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="contractTypeMobile"
                          checked={contractType === item.key}
                          onChange={() => setContractType(item.key)}
                        />
                        {item.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-slate-800/80 border border-blue-200 dark:border-slate-700 rounded-xl p-3">
                  <p className="text-sm font-bold text-blue-800 dark:text-blue-200 mb-2">Modelo de trabalho</p>
                  <div className="space-y-2 text-sm text-slate-700 dark:text-slate-100">
                    {workModelConfig.map((item) => (
                      <label key={item.key} className="flex items-center gap-2 text-slate-700 dark:text-slate-100">
                        <input
                          type="radio"
                          name="workModelMobile"
                          checked={workModel === item.key}
                          onChange={() => setWorkModel(item.key)}
                          className="accent-blue-600 dark:accent-blue-400"
                        />
                        {item.label}
                      </label>
                    ))}
                  </div>
                </div>

                <fieldset
                  disabled={selectionProcessOnly}
                  className={`m-0 p-0 border-0 ${selectionProcessOnly ? "opacity-50" : ""}`}
                >
                  <WorkPeriodPicker
                    idPrefix="wp-mobile"
                    startMonth={workPeriodStartMonth}
                    setStartMonth={setWorkPeriodStartMonth}
                    startYear={workPeriodStartYear}
                    setStartYear={setWorkPeriodStartYear}
                    endMonth={workPeriodEndMonth}
                    setEndMonth={setWorkPeriodEndMonth}
                    endYear={workPeriodEndYear}
                    setEndYear={setWorkPeriodEndYear}
                    stillWorking={workPeriodStillWorking}
                    setStillWorking={setWorkPeriodStillWorking}
                  />
                </fieldset>

                <label className="mt-2 flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={Boolean(selectionProcessOnly)}
                    onChange={(e) => setSelectionProcessOnly(e.target.checked)}
                  />
                  <span>
                    <strong>Não se aplica</strong> — não fui contratado(a) por esta empresa
                    (avaliar apenas o processo seletivo).
                  </span>
                </label>
              </div>

              {selectedCompanyData && (
                <div className="mt-3 grid grid-cols-1 gap-3">
                  <div className="bg-white dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-xl p-3">
                    <p className="text-sm font-bold text-blue-800 dark:text-blue-200 mb-1">Entradas na empresa</p>
                    <p className="text-xs text-blue-600 dark:text-blue-300 mb-2">{getTopSliceLabel(sourcePieData)}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-24 rounded-full border border-gray-200 flex-shrink-0 aspect-square" style={{ background: `conic-gradient(${sourcePieData.chart})` }} />
                      <div className="space-y-1 text-xs">
                        {sourcePieData.items.map((item) => (
                          <p key={item.key} className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                            {item.label}: {item.percent.toFixed(0)}%
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-xl p-3">
                    <p className="text-sm font-bold text-blue-800 dark:text-blue-200 mb-1">Classificação profissional da empresa</p>
                    <p className="text-xs text-blue-600 dark:text-blue-300 mb-2">{getTopSliceLabel(contractPieData)}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-24 rounded-full border border-gray-200 flex-shrink-0 aspect-square" style={{ background: `conic-gradient(${contractPieData.chart})` }} />
                      <div className="space-y-1 text-xs">
                        {contractPieData.items.map((item) => (
                          <p key={item.key} className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                            {item.label}: {item.percent.toFixed(0)}%
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-xl p-3">
                    <p className="text-sm font-bold text-blue-800 dark:text-blue-200 mb-1">Modelo de trabalho na empresa</p>
                    <p className="text-xs text-blue-600 dark:text-blue-300 mb-2">{getTopSliceLabel(workModelPieData)}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-24 rounded-full border border-gray-200 flex-shrink-0 aspect-square" style={{ background: `conic-gradient(${workModelPieData.chart})` }} />
                      <div className="space-y-1 text-xs">
                        {workModelPieData.items.map((item) => (
                          <p key={`company_work_model_${item.key}`} className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                            {item.label}: {item.percent.toFixed(0)}%
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {/* Barra de progresso dos critérios */}
              {!selectionProcessOnly && visibleCriterionIdx >= 0 && (
                <div
                  className="sticky z-10 mb-2 mx-auto w-full max-w-[380px] bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-lg border border-blue-100 dark:border-slate-700 px-3 py-2 shadow-sm"
                  style={{ top: stickyProgressTop, left: 0, right: 0 }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-blue-800 dark:text-blue-200">Critério {visibleCriterionIdx + 1} de {campos.length}</span>
                    <span className="text-xs text-slate-500">{Math.round(((visibleCriterionIdx + 1) / campos.length) * 100)}%</span>
                  </div>
                  <div className="w-full h-2 bg-blue-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-blue-700 rounded-full transition-all duration-300" style={{ width: `${((visibleCriterionIdx + 1) / campos.length) * 100}%` }} />
                  </div>
                </div>
              )}

              {selectionProcessOnly ? (
                <SelectionProcessReviewForm
                  clarity={spClarity}
                  setClarity={setSpClarity}
                  communication={spCommunication}
                  setCommunication={setSpCommunication}
                  responseTime={spResponseTime}
                  setResponseTime={setSpResponseTime}
                  discriminationFelt={spDiscriminationFelt}
                  setDiscriminationFelt={setSpDiscriminationFelt}
                  discriminationComment={spDiscriminationComment}
                  setDiscriminationComment={setSpDiscriminationComment}
                  evidenceFiles={spEvidenceFiles}
                  setEvidenceFiles={setSpEvidenceFiles}
                />
              ) : (
                campos.map((campo, idx) => (
                <div key={idx} ref={el => criterionRefs.current[idx] = el} data-criterion-idx={idx} className={`bg-gray-50 dark:bg-slate-800 p-3 rounded-xl border ${
                  showValidation && !isCampoFilled(campo)
                    ? "border-red-500 ring-2 ring-red-400"
                    : "border-gray-200 dark:border-slate-700"
                }`}>
                  <label className="text-slate-700 dark:text-blue-200 font-semibold flex items-center gap-2 text-sm">
                    <span className={`w-9 h-9 rounded-xl border bg-gradient-to-br ${campo.iconBg} flex items-center justify-center shadow-sm`}>
                      {campo.icon}
                    </span>
                    <span>
                      <span className="flex items-center gap-1.5 flex-wrap">
                        <span>{campo.label}</span>
                        {campo.restrictedNote && (
                          <span
                            title={campo.restrictedNote}
                            aria-label={campo.restrictedNote}
                            className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-full px-2 py-0.5 cursor-help"
                          >
                            <FiInfo className="w-3 h-3" aria-hidden="true" />
                            <span>Restrito a especialistas</span>
                          </span>
                        )}
                      </span>
                      {campo.subtitle && <span className="block text-xs text-slate-500 dark:text-blue-300">{campo.subtitle}</span>}
                    </span>
                  </label>
                  {campo.type === "yesno"
                    ? renderYesNo(campo.value, campo.set, campo.comment, campo.setComment, campo.label)
                    : renderStars(campo.value, campo.set, campo.comment, campo.setComment, campo.label, campo.restrictKey)}
                </div>
                ))
              )}
            </div>

            <div className="bg-gray-50 dark:bg-slate-800 p-3 rounded-xl border border-gray-200 dark:border-slate-700">
              <label className="text-slate-700 dark:text-blue-200 font-semibold text-sm block mb-2">Algo que queira acrescentar?</label>
              <RestrictableTextarea
                guidanceText={COMMENT_GUIDANCE_TEXT}
                warningText={COMMENT_WARNING_TEXT}
                containsPossiblePersonName={containsPossiblePersonName}
                className="w-full p-3 border border-gray-300 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-100 bg-white dark:bg-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Descreva sua experiência na empresa..."
                rows={3}
                value={generalComment}
                onValueChange={setGeneralComment}
                segments={generalCommentRestrictedSegments}
                onSegmentsChange={setGeneralCommentRestrictedSegments}
              />
            </div>

            {error && <p className="text-red-600 text-center text-xs font-medium">{error}</p>}

            <style>{`
              @keyframes ctaGlow {
                0%, 100% { box-shadow: 0 0 8px rgba(59,130,246,0.4); }
                50% { box-shadow: 0 0 20px rgba(59,130,246,0.8), 0 0 40px rgba(59,130,246,0.3); }
              }
            `}</style>
            <button type="submit"
              onClick={() => { findHelpAfterSubmitRef.current = false; }}
              className={`w-full py-3 rounded-xl font-bold text-white transition-all ${
                isAuthenticated ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-600"
              }`}
              style={!isAuthenticated ? { animation: 'ctaGlow 2s ease-in-out infinite' } : undefined}
              disabled={!isAuthenticated || isLoading}
            >
              {isLoading ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <span
                    aria-hidden="true"
                    className="inline-block h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin"
                  />
                  <span>Enviando</span>
                </span>
              ) : isAuthenticated ? (
                "Enviar Avaliação"
              ) : (
                "Faça login para avaliar"
              )}
            </button>
            {((commentRating && containsPossiblePersonName(commentRating)) ||
              (generalComment && containsPossiblePersonName(generalComment)) ||
              Object.values(campos).some(c => c.comment && containsPossiblePersonName(c.comment))) && (
              <p className="text-yellow-700 dark:text-yellow-300 text-xs text-center mt-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/60 rounded px-2 py-1">
                Identificamos possível citação de nome em seu comentário. Você pode enviar normalmente; consideramos apenas um aviso para revisão.
              </p>
            )}
            <p className="text-blue-600 dark:text-blue-300 text-xs font-semibold text-center mt-2">
              Sua opinião é anônima e ajuda outros profissionais
            </p>
          </form>
        </section>

        {/* RANKING */}
        <section className="bg-white dark:bg-slate-900 rounded-2xl shadow-md p-5 border border-blue-50 dark:border-slate-700">
          <h2 className="text-sm uppercase tracking-[0.14em] font-extrabold text-blue-800 dark:text-blue-200 text-center mb-3">🏆 Ranking</h2>
          {Array.isArray(setoresList) && setoresList.length > 0 && (
            <select
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e.target.value)}
              className="w-full mb-3 p-2 text-sm border border-blue-200 rounded-xl bg-white dark:bg-slate-800 text-slate-700 dark:text-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">Todos os setores</option>
              {setoresList.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
          {Array.isArray(segmentosList) && segmentosList.length > 0 && (
            <select
              value={segmentFilter}
              onChange={(e) => setSegmentFilter(e.target.value)}
              className="w-full mb-3 p-2 text-sm border border-blue-200 rounded-xl bg-white dark:bg-slate-800 text-slate-700 dark:text-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">Todos os segmentos (CNAE)</option>
              {segmentosList.map((seg) => {
                const opt = (cnaeSegmentOptions || []).find((item) => item.code === seg);
                return <option key={seg} value={seg}>{opt ? opt.label : seg}</option>;
              })}
            </select>
          )}
          {Array.isArray(top3) && top3.length > 0 && (
            <div className="mb-4 space-y-2">
              {top3.map((emp, i) => {
                const media = calcularMedia(emp);
                const isUnrated = media === "--";
                const mediaValue = Number.parseFloat(media);
                const isRecommendedCompany = !isUnrated && Number.isFinite(mediaValue) && mediaValue >= 3;
                return (
                  <div key={i} className={`${isUnrated ? "bg-slate-200 text-slate-600" : `bg-gradient-to-r ${getMedalColor(i)} text-white`} rounded-xl p-3 flex justify-between items-center`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xl">{getMedalEmoji(i)}</span>
                      <div className="min-w-0">
                        <p className="font-bold text-sm truncate max-w-[120px]">{emp.company}</p>
                        {!isUnrated && (
                          <p className={`text-[10px] font-bold ${isUnrated ? "text-slate-600" : "text-white/90"}`}>
                            {isRecommendedCompany ? "✓ Acima da média" : "X Abaixo da média"}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className={`${isUnrated ? "bg-slate-300 text-slate-700" : "bg-white/20 text-white"} px-2 py-1 rounded-lg font-bold text-xs`}>
                      {isUnrated ? "--" : `${media} ⭐`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* AUTOCOMPLETAÇÃO */}
        <section className="bg-white dark:bg-slate-900 rounded-2xl shadow-md p-5 border border-blue-50 dark:border-slate-700">
          <h2 className="text-sm uppercase tracking-[0.12em] font-extrabold text-blue-800 dark:text-blue-200 text-center mb-2">🏢 Empresas por busca</h2>
          <p className="text-sm text-slate-700 dark:text-blue-100 text-center leading-relaxed">
            Para encontrar empresas em bases maiores, use o campo
            <span className="font-semibold"> "Selecione a Empresa"</span> acima.
            Digite parte do nome e escolha na lista sugerida.
          </p>
        </section>
      </main>

      <CaptchaModal
        open={showCaptcha}
        onClose={() => setShowCaptcha(false)}
        checked={captchaConfirmed}
        onChange={setCaptchaConfirmed}
        onConfirm={() => {
          if (typeof handleCaptchaConfirmed === "function") {
            handleCaptchaConfirmed();
          } else {
            setCaptchaConfirmed(true);
            setShowCaptcha(false);
          }
        }}
      />

      <footer className="text-center text-xs text-slate-500 dark:text-slate-300 mt-6">
        <div className="space-x-2">
          <Link to="/termos-de-uso" className="text-blue-600 dark:text-blue-300 hover:underline font-semibold">
            Termos de Uso
          </Link>
          <span>•</span>
          <a href="/politica-de-privacidade" className="text-blue-600 dark:text-blue-300 hover:underline font-semibold">
            Política de Privacidade
          </a>
          <span>•</span>
          <Link to="/purpose" className="text-blue-600 dark:text-blue-300 hover:underline">
            Qual o nosso propósito?
          </Link>
        </div>
        <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500">
          Trabalhei Lá | CNPJ: 67.029.282/0001-20
        </p>
      </footer>
      <PaymentInfoModal
        open={showPaymentInfo}
        onClose={() => setShowPaymentInfo(false)}
        audience="both"
      />
      <EvaluationConfirmModal
        open={showConfirmModal}
        onConfirm={handleConfirmSend}
        onReview={() => setShowConfirmModal(false)}
      />
    </div>
  );
}

export default TrabalheiLaMobile;